import React, { useState, useMemo } from 'react';
import {
    Card, Box, Typography, Tabs, Tab, Button,
} from '@mui/material';
import { Add } from '@mui/icons-material';
import { TestEntityCard } from './TestEntityCard';
import { NewEntityCard } from './NewEntityCard';
import { EmptyState } from '../common/EmptyState';

enum RelevanceScore {
    NotScored = 'NotScored',
    NotRelevant = 'NotRelevant',
    Relevant = 'Relevant',
    HighlyRelevant = 'HighlyRelevant',
}

interface PropTypes {
    scoredEntities: Models.IngestConfigApi.RelevanceTests.ScoredEntity[];
    handleCommentChange: (
        entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
        comment: string
    ) => void;
    handleScoredEntityChange: (
        entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity,
        score: Models.IngestConfigApi.RelevanceTests.RelevanceScore
    ) => void;
    markChanged: () => void;
    productKey: string;
    culture: string;
    onEntityAdded: (entity: Models.IngestConfigApi.RelevanceTests.ScoredEntity) => void;
}

export const CurrentScoredEntities: React.FC<PropTypes> = ({
    scoredEntities,
    handleCommentChange,
    handleScoredEntityChange,
    markChanged,
    productKey,
    culture,
    onEntityAdded,
}) => {
    const [currentTab, setCurrentTab] = useState(0);
    const [showEmptyCard, setShowEmptyCard] = useState(false);

    const entityGroups = useMemo(() => {
        const all = scoredEntities;
        const highlyRelevant = scoredEntities.filter((e) => e.relevance === RelevanceScore.HighlyRelevant);
        const relevant = scoredEntities.filter((e) => e.relevance === RelevanceScore.Relevant);
        const notRelevant = scoredEntities.filter((e) => e.relevance === RelevanceScore.NotRelevant);

        return [all, highlyRelevant, relevant, notRelevant];
    }, [scoredEntities]);

    const currentEntities = entityGroups[currentTab];

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number): void => {
        setCurrentTab(newValue);
    };

    const handleAddConcept = (): void => {
        setCurrentTab(0);
        setShowEmptyCard((prev) => !prev);
        markChanged();
    };

    const getScoredEntityColor = (score: RelevanceScore): string => {
        switch (score) {
            case RelevanceScore.HighlyRelevant:
                return '#e8f5e9'; // Light green
            case RelevanceScore.Relevant:
                return '#fff8e1'; // Light yellow
            case RelevanceScore.NotRelevant:
                return '#ffebee'; // Light red
            default:
                return '#f5f5f5'; // Light grey
        }
    };

    return (
        <Card>
            <Box sx={{
                display: 'flex', flexDirection: 'row', alignItems: 'start', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', p: 2,
            }}
            >
                <Box>
                    <Typography variant="h6">
                        Current Scored Entities
                    </Typography>
                </Box>
                <Button startIcon={(<Add />)} variant="outlined" onClick={handleAddConcept}>Add</Button>
            </Box>
            <Box sx={{ p: 2 }}>
                <Tabs value={currentTab} onChange={handleTabChange}>
                    <Tab label={`All (${entityGroups[0].length})`} />
                    <Tab label={`Highly Relevant (${entityGroups[1].length})`} />
                    <Tab label={`Relevant (${entityGroups[2].length})`} />
                    <Tab label={`Not Relevant (${entityGroups[3].length})`} />
                </Tabs>
                <Box sx={{ mt: 2, maxHeight: '600px', overflowY: 'auto' }}>
                    {currentTab === 0 && showEmptyCard && (
                        <NewEntityCard
                            culture={culture}
                            productKey={productKey}
                            scoredEntities={scoredEntities}
                            onEntityAdded={(entity): void => {
                                onEntityAdded(entity);
                                setShowEmptyCard(false);
                            }}
                        />
                    )}

                    {currentTab === 0 && entityGroups[0].length === 0 ? (
                        <EmptyState sx={{ p: 4 }}>
                            <Typography gutterBottom variant="h6">
                                No Scored Concepts
                            </Typography>
                            <Typography variant="body1">
                                Start scoring concepts in the right panel to get started
                            </Typography>
                        </EmptyState>
                    ) : (
                        currentEntities.map((entity) => (
                            <TestEntityCard
                                showCommentBox
                                showScoreControls
                                backgroundColor={getScoredEntityColor(entity.relevance)}
                                entity={entity}
                                handleCommentChange={handleCommentChange}
                                handleScoredEntityChange={handleScoredEntityChange}
                                key={entity.designConceptId}
                                markChanged={markChanged}
                                productKey={productKey}
                            />
                        ))
                    )}
                </Box>
            </Box>
        </Card>
    );
};
